import { useEffect, useMemo, useState } from "react";
import { trpc } from "../../../utils/trpc";
import { UserPublicationsContext } from "./UserPublicationsContext";
import type { Publication } from "@rowan/prisma";

export const UserPublicationsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [publicationsBySlug, setPublicationsBySlug] =
    useState<Record<string, Publication>>();
  const [publications, setPublications] = useState<Publication[]>();
  const [archivedPublications, setArchivedPublications] =
    useState<Publication[]>();

  const { data, isLoading, isFetched } = trpc.getPublications.useQuery();
  const { data: archivedData, isLoading: archivedIsLoading } =
    trpc.getArchivedPublications.useQuery();

  useEffect(() => {
    if (data) {
      const userPublications = data.map((publication) => ({
        ...publication,
        lastModified: new Date(publication.lastModified),
        createdAt: new Date(publication.createdAt),
      }));
      const userPublicationsBySlug = userPublications.reduce(
        (acc, publication) => {
          const slug = publication.filename;
          if (slug) {
            acc[slug] = publication;
          }
          return acc;
        },
        {} as Record<string, Publication>,
      );
      setPublicationsBySlug(userPublicationsBySlug);
      setPublications(userPublications);
    }
  }, [data]);

  useEffect(() => {
    if (archivedData) {
      const userPublications = archivedData.map((publication) => ({
        ...publication,
        lastModified: new Date(publication.lastModified),
        createdAt: new Date(publication.createdAt),
      }));

      setArchivedPublications(userPublications);
    }
  }, [archivedData]);

  const context = useMemo(
    () => ({
      publications,
      archivedPublications,
      publicationsBySlug,
      isLoading: isLoading || archivedIsLoading,
      isFetched,
    }),
    [
      publications,
      archivedPublications,
      publicationsBySlug,
      isLoading,
      archivedIsLoading,
      isFetched,
    ],
  );

  return (
    <UserPublicationsContext.Provider value={context}>
      {children}
    </UserPublicationsContext.Provider>
  );
};
